import React, { useState, useEffect, useRef, useContext } from 'react';
import { 
    ImageIcon, Save, Trash2, Link, Unlink, MousePointer2, Move, AlertTriangle, 
    ChevronDown, ChevronUp, Plus, Type, CheckSquare, Maximize2, ArrowRightLeft, Tags, Settings
} from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import ImageEditorModal from './ImageEditorModal';

const AVAILABLE_FIELDS = [
    { key: 'kw_num', label: 'Kalenderwoche' }, 
    { key: 'date_start', label: 'Datum von' }, 
    { key: 'date_end', label: 'Datum bis' },
    { key: 'botanical_name', label: 'Botanischer Name' }, { key: 'german_name', label: 'Deutscher Name' },
    { key: 'family', label: 'Familie' }, { key: 'growth_form', label: 'Wuchsform' },
    { key: 'artname_erklärung', label: 'Artname Erklärung' }, { key: 'origin', label: 'Heimat' },
    { key: 'distribution', label: 'Natürlicher Standort' }, { key: 'is_zuechtung', label: 'Check: Züchtung' },
    { key: 'is_wildpflanze', label: 'Check: Wildpflanze' }, { key: 'is_heimisch', label: 'Check: Heimisch' },
    { key: 'is_protected', label: 'Check: Geschützt' }, { key: 'endangerment', label: 'Gefährdungsgrad' },
    { key: 'observed_location', label: 'Beobachteter Standort' }, { key: 'height', label: 'Höhe' },
    { key: 'width', label: 'Breite' }, { key: 'height_width', label: 'Höhe/Breite kombiniert' },
    { key: 'wuchs', label: 'Wuchs (Freitext)' }, { key: 'leaf_info', label: 'Blattinfo' },
    { key: 'autumn_color', label: 'Herbstfärbung' }, { key: 'life_zone', label: 'Lebensbereich' },
    { key: 'flower_color', label: 'Blütenfarbe' }, { key: 'flower_inflorescence', label: 'Blütenstand' },
    { key: 'fruit_info', label: 'Früchte' }, { key: 'care_notes', label: 'Pflegehinweise' },
    { key: 'uses', label: 'Verwendung' }, { key: 'soil_type', label: 'Bodenart' },
    { key: 'nutrient_needs', label: 'Nährstoffbedarf' }, { key: 'soil_ph', label: 'Boden pH' },
    { key: 'image_1', label: 'Bild 1' }, { key: 'image_2', label: 'Bild 2' },
    { key: 'signature_name', label: 'Signatur: Azubi' }, 
    { key: 'signature_instructor', label: 'Signatur: Ausbilder' },
    { key: 'signature_date', label: 'Signatur: Datum' }
];

const PDF_WIDTH = 595.276;
const PDF_HEIGHT = 841.89;

// Helper for deterministic group colors
const getGroupColor = (name) => {
    if (!name) return null;
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${Math.abs(hash % 360)}, 75%, 45%)`;
};

export default function PdfOverlayEditor({ formData, setFormData, coordinates: initialCoordinates, onImageUpload, image1Preview, image2Preview }) {
    const { user, fetchWithAuth } = useContext(AuthContext);
    const isAdmin = user?.is_staff || user?.is_superuser;
    const isFelix = user?.username?.toLowerCase() === 'felix_brabetz';

    // Only admins or Felix can calibrate.
    const canCalibrate = isAdmin || isFelix || (window.location.hostname === 'localhost');

    useEffect(() => {
        if (user) {
            console.log("PDF Editor Debug:", { 
                username: user.username,
                isAdmin, 
                isFelix,
                canCalibrate,
                hostname: window.location.hostname
            });
        }
    }, [user, isAdmin, isFelix, canCalibrate]);

    const [localCoords, setLocalCoords] = useState(initialCoordinates);
    const [containerWidth, setContainerWidth] = useState(0);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [editingImageKey, setEditingImageKey] = useState(null); // 'image_1' or 'image_2'
    const containerRef = useRef(null);

    // Debug & Calibration State
    const [debugMode, setDebugMode] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [selectedFields, setSelectedFields] = useState(new Set());
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
    const [dragSnapshot, setDragSnapshot] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);

    // Grid Status
    const [showGridInEditor, setShowGridInEditor] = useState(true);
    const [showGridInExport, setShowGridInExport] = useState(() => {
        return localStorage.getItem('pdfGridInExport') === 'true';
    });

    useEffect(() => {
        localStorage.setItem('pdfGridInExport', showGridInExport);
    }, [showGridInExport]);

    // Background Calibration
    const [bgOffsetV, setBgOffsetV] = useState(() => {
        return parseFloat(localStorage.getItem('pdfBgOffsetV')) || 0;
    });
    const [bgOffsetH, setBgOffsetH] = useState(() => {
        return parseFloat(localStorage.getItem('pdfBgOffsetH')) || 0;
    });
    const [bgScaleV, setBgScaleV] = useState(() => {
        return parseFloat(localStorage.getItem('pdfBgScaleV')) || 1;
    });
    const [bgScaleH, setBgScaleH] = useState(() => {
        return parseFloat(localStorage.getItem('pdfBgScaleH')) || 1;
    });

    // Global Export Nudge (actually affects the coordinates file metadata)
    const [exportNudgeX, setExportNudgeX] = useState(0);
    const [exportNudgeY, setExportNudgeY] = useState(0);
    const [exportScaleX, setExportScaleX] = useState(1);
    const [exportScaleY, setExportScaleY] = useState(1);

    useEffect(() => {
        if (initialCoordinates?._metadata) {
            setExportNudgeX(initialCoordinates._metadata.export_offset_x || 0);
            setExportNudgeY(initialCoordinates._metadata.export_offset_y || 0);
            setExportScaleX(initialCoordinates._metadata.export_scale_x || 1);
            setExportScaleY(initialCoordinates._metadata.export_scale_y || 1);
        }
    }, [initialCoordinates]);

    useEffect(() => {
        localStorage.setItem('pdfBgOffsetV', bgOffsetV);
        localStorage.setItem('pdfBgOffsetH', bgOffsetH);
        localStorage.setItem('pdfBgScaleV', bgScaleV);
        localStorage.setItem('pdfBgScaleH', bgScaleH);
    }, [bgOffsetV, bgOffsetH, bgScaleV, bgScaleH]);

    // Lasso Selection State
    const [selectionFrame, setSelectionFrame] = useState({ active: false, x1: 0, y1: 0, x2: 0, y2: 0 });

    // Movable Calibrator State
    const [calibratorPos, setCalibratorPos] = useState({ x: 50, y: 50 });
    const [isMinimized, setIsMinimized] = useState(false);
    const [isDraggingCalibrator, setIsDraggingCalibrator] = useState(false);
    const [calibratorDragStart, setCalibratorDragStart] = useState({ x: 0, y: 0 });

    // Collapsible Groups
    const [collapsedSections, setCollapsedSections] = useState({
        add: false,
        raster: false,
        bg: false,
        export: false,
        field: false
    });

    const toggleSection = (section) => {
        setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    useEffect(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
        }
        const handleResize = () => {
            if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
        };
        
        // Keyboard Shortcut: Alt + Shift + C to toggle debug mode
        const handleKeyDown = (e) => {
            // Universal Shortcut (Fallback)
            const isShortcut = (e.altKey && e.shiftKey && e.code === 'KeyC') || (e.ctrlKey && e.altKey && e.code === 'KeyS');
            if (isShortcut) {
                e.preventDefault();
                setDebugMode(prev => !prev);
                console.info("PDF Calibrator toggled via keyboard shortcut");
            }
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('keydown', handleKeyDown);
        
        if (canCalibrate) {
            console.info("PDF Overlay Editor: Admin/Dev access detected.");
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [canCalibrate]);

    const scale = containerWidth / PDF_WIDTH;

    // Mapping from localCoords to FormData
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name.startsWith('month_')) {
            const monthArr = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
            const mIdx = parseInt(name.replace('month_', '')) - 1;
            const numeral = monthArr[mIdx];
            const currentMonths = formData.flowering_months || [];
            if (checked) {
                setFormData(prev => ({ ...prev, flowering_months: [...currentMonths, numeral] }));
            } else {
                setFormData(prev => ({ ...prev, flowering_months: currentMonths.filter(m => m !== numeral) }));
            }
            return;
        }

        if (name.startsWith('loc_')) {
            const opt = name.replace('loc_', '');
            const current = formData.location_preferences || [];
            if (checked) {
                setFormData(prev => ({ ...prev, location_preferences: [...current, opt] }));
            } else {
                setFormData(prev => ({ ...prev, location_preferences: current.filter(o => o !== opt) }));
            }
            return;
        }

        if (name.startsWith('water_')) {
            const opt = name.replace('water_', '');
            const current = formData.watering_needs || [];
            const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
            if (checked) {
                setFormData(prev => ({ ...prev, watering_needs: [...currentArray, opt] }));
            } else {
                setFormData(prev => ({ ...prev, watering_needs: currentArray.filter(o => o !== opt) }));
            }
            return;
        }

        if (name.startsWith('social_')) {
            const opt = name.replace('social_', '');
            const current = formData.social_preferences || [];
            const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
            if (checked) {
                setFormData(prev => ({ ...prev, social_preferences: [...currentArray, opt] }));
            } else {
                setFormData(prev => ({ ...prev, social_preferences: currentArray.filter(o => o !== opt) }));
            }
            return;
        }

        if (name.startsWith('lime_')) {
            const opt = name.replace('lime_', '');
            const current = formData.lime_tolerance || [];
            const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
            if (checked) {
                setFormData(prev => ({ ...prev, lime_tolerance: [...currentArray, opt] }));
            } else {
                setFormData(prev => ({ ...prev, lime_tolerance: currentArray.filter(o => o !== opt) }));
            }
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleContainerMouseMove = (e) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;
        setMousePos({ x, y });

        if (canCalibrate) {
            if (selectionFrame.active) {
                setSelectionFrame(prev => ({ ...prev, x2: x, y2: y }));
                return;
            }

            const dx = x - dragStartPos.x;
            const dy = y - dragStartPos.y;

            if (isDragging) {
                const updated = { ...localCoords };
                selectedFields.forEach(key => {
                    if (dragSnapshot[key]) {
                        // Compensate for global scaling when dragging
                        // raw_delta = mouse_delta_in_pt / global_scale
                        updated[key] = {
                            ...updated[key],
                            x: dragSnapshot[key].x + (dx / exportScaleX),
                            y: dragSnapshot[key].y + (dy / exportScaleY)
                        };
                    }
                });
                setLocalCoords(updated);
            } else if (isResizing && selectedFields.size === 1) {
                const key = Array.from(selectedFields)[0];
                if (dragSnapshot[key]) {
                    setLocalCoords(prev => ({
                        ...prev,
                        [key]: {
                            ...prev[key],
                            w: Math.max(5, dragSnapshot[key].w + (dx / exportScaleX)),
                            h: Math.max(5, dragSnapshot[key].h + (dy / exportScaleY))
                        }
                    }));
                }
            }
        }
    };

    const handleFieldMouseDown = (e, key) => {
        if (!debugMode || !canCalibrate) return;
        e.stopPropagation();
        e.preventDefault();

        let newSelected = new Set(selectedFields);

        if (e.shiftKey) {
            if (newSelected.has(key)) newSelected.delete(key);
            else newSelected.add(key);
        } else {
            // Check if part of existing selection
            if (!newSelected.has(key)) {
                // If the field belongs to a group, select the whole group automatically
                const targetGroup = localCoords[key].group;
                if (targetGroup) {
                    newSelected = new Set(
                        Object.keys(localCoords).filter(k => localCoords[k].group === targetGroup)
                    );
                } else {
                    newSelected = new Set([key]);
                }
            }
        }
        setSelectedFields(newSelected);

        // Prepare for dragging
        setIsDragging(true);
        setDragStartPos({ x: mousePos.x, y: mousePos.y });
        const snapshot = {};
        newSelected.forEach(k => {
            snapshot[k] = { x: localCoords[k].x, y: localCoords[k].y };
        });
        setDragSnapshot(snapshot);
    };

    const handleGlobalMouseUp = (e) => {
        if (selectionFrame.active) {
            const rect = {
                x: Math.min(selectionFrame.x1, selectionFrame.x2),
                y: Math.min(selectionFrame.y1, selectionFrame.y2),
                w: Math.abs(selectionFrame.x1 - selectionFrame.x2),
                h: Math.abs(selectionFrame.y1 - selectionFrame.y2)
            };

            const newlySelected = new Set(e.shiftKey ? selectedFields : []);
            
            // Minimal selection size to avoid accidental clicks clearing selection
            if (rect.w > 2 || rect.h > 2) {
                Object.entries(localCoords).forEach(([key, coord]) => {
                    // Check if center of field is in lasso
                    const cx = coord.x + coord.w / 2;
                    const cy = coord.y + coord.h / 2;
                    if (cx >= rect.x && cx <= rect.x + rect.w && cy >= rect.y && cy <= rect.y + rect.h) {
                        newlySelected.add(key);
                    }
                });
                setSelectedFields(newlySelected);
            }
            setSelectionFrame({ active: false, x1: 0, y1: 0, x2: 0, y2: 0 });
        }

        setIsDragging(false);
        setIsResizing(false);
        setIsDraggingCalibrator(false);
    };

    useEffect(() => {
        if (isDragging || isResizing || isDraggingCalibrator || selectionFrame.active) {
            window.addEventListener('mouseup', handleGlobalMouseUp);
            window.addEventListener('mousemove', handleContainerMouseMove);
        }
        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            window.removeEventListener('mousemove', handleContainerMouseMove);
        };
    }, [isDragging, isResizing, isDraggingCalibrator, selectionFrame.active]);

    const handleSaveGlobal = async () => {
        if (!canCalibrate) return;
        setIsSaving(true);
        setSaveStatus('Saving...');
        try {
            const dataToSave = {
                ...localCoords,
                _metadata: {
                    export_offset_x: exportNudgeX,
                    export_offset_y: exportNudgeY,
                    export_scale_x: exportScaleX,
                    export_scale_y: exportScaleY
                }
            };
            const response = await fetchWithAuth('/api/pdf-coordinates/update/', {
                method: 'POST',
                body: JSON.stringify(dataToSave)
            });
            if (response.ok) {
                setSaveStatus('Saved successfully!');
                setTimeout(() => setSaveStatus(null), 3000);
            } else {
                setSaveStatus('Error saving coordinates.');
            }
        } catch (err) {
            setSaveStatus('Connection error.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleGroupAction = (action) => {
        if (selectedFields.size < 2 && action === 'group') return;
        const updated = { ...localCoords };
        
        if (action === 'group') {
            const groupName = prompt("Name der neuen Gruppe?", `group_${Date.now()}`);
            if (!groupName) return;
            selectedFields.forEach(k => {
                updated[k] = { ...updated[k], group: groupName };
            });
        } else if (action === 'ungroup') {
            selectedFields.forEach(k => {
                delete updated[k].group;
            });
        }
        
        setLocalCoords(updated);
        setSaveStatus("Änderungen noch nicht gespeichert!");
    };

    const handleSelectAll = (e) => {
        e.preventDefault();
        const allKeys = Object.keys(localCoords).filter(k => !k.startsWith('_'));
        setSelectedFields(new Set(allKeys));
    };

    const handleAddElement = (type) => {
        const key = prompt(`Name für das neue ${type === 'checkbox' ? 'Checkbox' : 'Textfeld'}?`);
        if (!key) return;
        
        if (localCoords[key]) {
            alert("Dieser Name existiert bereits!");
            return;
        }

        const newElement = {
            type: type,
            x: 100,
            y: 100,
            w: type === 'checkbox' ? 12 : 150,
            h: type === 'checkbox' ? 12 : 20,
            group: null
        };

        setLocalCoords(prev => ({
            ...prev,
            [key]: newElement
        }));
        setSelectedFields(new Set([key]));
        setSaveStatus("Neues Feld hinzugefügt! Bitte positionieren und GLEICH SPEICHERN.");
    };

    const handleDeleteElement = (key) => {
        if (!confirm(`Möchtest du das Feld '${key}' wirklich löschen?`)) return;
        const updated = { ...localCoords };
        delete updated[key];
        setLocalCoords(updated);
        setSelectedFields(new Set());
        setSaveStatus("Feld gelöscht. Bitte SPEICHERN.");
    };

    const handleCalibratorMouseDown = (e) => {
        if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) return; 
        setIsDraggingCalibrator(true);
        setCalibratorDragStart({
            x: e.clientX - calibratorPos.x,
            y: e.clientY - calibratorPos.y
        });
    };

    const handleRemap = (oldKey, newKey) => {
        if (!newKey || oldKey === newKey) return;
        if (localCoords[newKey]) {
            if (!confirm(`Das Feld '${newKey}' existiert bereits. Überschreiben?`)) return;
        }

        const updated = { ...localCoords };
        updated[newKey] = { ...updated[oldKey] };
        delete updated[oldKey];

        setLocalCoords(updated);
        setSelectedFields(new Set([newKey]));
        setSaveStatus(`Feld zu '${newKey}' umgemappt. Bitte SPEICHERN.`);
    };

    const updateManualCoord = (key, field, val) => {
        const numVal = parseFloat(val);
        if (isNaN(numVal)) return;

        setLocalCoords(prev => {
            const updated = { ...prev };
            // If field is part of a selection, update all selected members
            const updateTargets = selectedFields.has(key) ? Array.from(selectedFields) : [key];
            
            updateTargets.forEach(targetKey => {
                updated[targetKey] = {
                    ...updated[targetKey],
                    [field]: numVal
                };
            });
            return updated;
        });
        setSaveStatus("Manuelle Änderung. Bitte SPEICHERN.");
    };

    const renderField = (key, coord) => {
        const isSelected = selectedFields.has(key);
        const groupColor = getGroupColor(coord.group);

        // Template offset from backend (no longer added to CSS to avoid doubling the shift, 
        // since the visual PNG is matched to the MediaBox by default)
        const templateOffset = localCoords._template_offset || { x: 0, y: 0 };
        // const tx = templateOffset.x;
        // const ty = templateOffset.y;

        const style = {
            position: 'absolute',
            // WYSIWYG: Boxes NOW reflect exportNudge/Scale in the editor.
            // DO NOT ADD tx/ty HERE. Backend translate handles the MediaBox offset natively!
            left: `${((coord.x + (coord.nx || 0)) * exportScaleX + exportNudgeX) * scale}px`,
            top: `${((coord.y + (coord.ny || 0)) * exportScaleY + exportNudgeY) * scale}px`,
            width: `${coord.w * exportScaleX * scale}px`,
            height: `${coord.h * exportScaleY * scale}px`,
            fontSize: `${(coord.fs || 9) * exportScaleY * scale}px`, // Scale font too for visual accuracy
            border: debugMode 
                ? (isSelected ? '2px solid #ef4444' : (groupColor ? `1.5px solid ${groupColor}` : '0.5px solid #666'))
                : '1px solid transparent',
            backgroundColor: debugMode 
                ? (isSelected ? 'rgba(239, 68, 68, 0.2)' : (groupColor ? `${groupColor}10` : 'transparent'))
                : 'rgba(34, 139, 34, 0.05)',
            zIndex: isSelected ? 100 : (debugMode ? 50 : 10),
            cursor: debugMode ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: (isDragging || isResizing) ? 'none' : 'all 0.1s ease',
        };

        const debugLabel = debugMode && (
            <div className={`absolute -top-4 left-0 text-white text-[7px] px-1 rounded-t-sm whitespace-nowrap pointer-events-none ${isSelected ? 'bg-red-600' : 'bg-gray-800'}`}>
                {key} {coord.group ? `[${coord.group}]` : ''}
            </div>
        );

        // Core Field Render
        let content = null;
        if (coord.type === 'image') {
            const num = key === 'image_1' ? 1 : 2;
            const preview = num === 1 ? image1Preview : image2Preview;
            const transform = formData?.[`${key}_transform`] || {};
            const isDeleted = transform.is_deleted === true;

            content = (
                <div 
                    className="w-full h-full flex flex-col items-center justify-center bg-plantlix-900/5 group-hover:bg-plantlix-100/30 transition-all overflow-hidden relative cursor-pointer"
                    onClick={() => !debugMode && setEditingImageKey(key)}
                >
                    {preview && !isDeleted ? (
                        <div className="w-full h-full relative">
                            <img 
                                src={preview} 
                                alt="Upload" 
                                className="w-full h-full object-contain transition-transform duration-200" 
                                style={{ 
                                    transform: `translate(${-(transform.crop?.x || 0)}%, ${-(transform.crop?.y || 0)}%) scale(${transform.zoom || 1}) rotate(${transform.rotation || 0}deg)`,
                                }}
                            />
                            {transform.is_deleted && (
                                <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center">
                                    <Trash2 className="text-red-500" size={24} />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-1 text-plantlix-500 opacity-40">
                            <ImageIcon style={{ width: `${14 * scale}px`, height: `${14 * scale}px` }} />
                        </div>
                    )}
                    <input id={`pdf-file-input-${num}`} type="file" accept="image/*" className="hidden" onChange={(e) => onImageUpload(e, key)} />
                </div>
            );
        } else if (coord.type === 'checkbox' || coord.type === 'radio') {
            // Helper for robust case-insensitive inclusion
            const includesCI = (arr, val) => {
                let items = [];
                if (Array.isArray(arr)) {
                    items = arr;
                } else if (typeof arr === 'string') {
                    // Split by comma or semicolon, then trim
                    items = arr.split(/[;,]/).map(s => s.trim());
                } else if (arr) {
                    items = [arr];
                }
                const searchVal = String(val).toLowerCase();
                return items.some(item => String(item).toLowerCase() === searchVal);
            };

            let checked = false;
            if (key.startsWith('month_')) {
                const m = key.replace('month_', '');
                const numerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
                const numeral = numerals[parseInt(m)-1];
                const current = formData.flowering_months || [];
                // Check for Roman numeral (VI), digit (6), OR full string match
                checked = includesCI(current, numeral) || includesCI(current, m);
            } else if (key.startsWith('social_')) {
                checked = includesCI(formData.social_preferences, key.replace('social_', ''));
            } else if (key.startsWith('water_')) {
                checked = includesCI(formData.watering_needs, key.replace('water_', ''));
            } else if (key.startsWith('lime_')) {
                checked = includesCI(formData.lime_tolerance, key.replace('lime_', ''));
            } else if (key.startsWith('loc_')) {
                checked = includesCI(formData.location_preferences, key.replace('loc_', ''));
            } else if (key.startsWith('ph_')) {
                checked = includesCI(formData.ph_preference, key.replace('ph_', ''));
            } else {
                checked = String(formData[key]) === '☑' || formData[key] === true;
            }

            content = (
                <div
                    className={`w-full h-full cursor-pointer flex items-center justify-center transition-all ${checked ? 'bg-plantlix-500/20 border-plantlix-500' : 'bg-red-500/5 border-red-500/30'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        const updatedData = { ...formData };
                        if (key.startsWith('month_')) {
                            const m = key.replace('month_', '');
                            const numerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
                            const numeral = numerals[parseInt(m)-1];
                            const current = Array.isArray(formData.flowering_months) 
                                ? formData.flowering_months 
                                : (typeof formData.flowering_months === 'string' ? formData.flowering_months.split(',').map(s => s.trim()) : []);
                            
                            updatedData.flowering_months = current.some(v => String(v).toLowerCase() === numeral.toLowerCase())
                                ? current.filter(i => String(i).toLowerCase() !== numeral.toLowerCase())
                                : [...current, numeral];
                        } else if (key.startsWith('loc_')) {
                            const val = key.replace('loc_', '');
                            const current = formData.location_preferences || [];
                            const items = Array.isArray(current) ? current : (typeof current === 'string' ? current.split(',').map(s => s.trim()) : []);
                            updatedData.location_preferences = items.some(v => String(v).toLowerCase() === val.toLowerCase())
                                ? items.filter(i => String(i).toLowerCase() !== val.toLowerCase())
                                : [...items, val];
                        } else if (key.startsWith('social_')) {
                            const val = key.replace('social_', '');
                            const current = formData.social_preferences || [];
                            const items = Array.isArray(current) ? current : (typeof current === 'string' ? current.split(',').map(s => s.trim()) : []);
                            updatedData.social_preferences = items.some(v => String(v).toLowerCase() === val.toLowerCase())
                                ? items.filter(i => String(i).toLowerCase() !== val.toLowerCase())
                                : [...items, val];
                        } else if (key.startsWith('water_')) {
                            const val = key.replace('water_', '');
                            const current = formData.watering_needs || [];
                            const items = Array.isArray(current) ? current : (typeof current === 'string' ? current.split(',').map(s => s.trim()) : []);
                            updatedData.watering_needs = items.some(v => String(v).toLowerCase() === val.toLowerCase())
                                ? items.filter(i => String(i).toLowerCase() !== val.toLowerCase())
                                : [...items, val];
                        } else if (key.startsWith('lime_')) {
                            const val = key.replace('lime_', '');
                            const current = formData.lime_tolerance || [];
                            const items = Array.isArray(current) ? current : (typeof current === 'string' ? current.split(',').map(s => s.trim()) : []);
                            updatedData.lime_tolerance = items.some(v => String(v).toLowerCase() === val.toLowerCase())
                                ? items.filter(i => String(i).toLowerCase() !== val.toLowerCase())
                                : [...items, val];
                        } else if (key.startsWith('ph_')) {
                            const val = key.replace('ph_', '');
                            const current = formData.ph_preference || [];
                            const items = Array.isArray(current) ? current : (typeof current === 'string' ? current.split(',').map(s => s.trim()) : []);
                            updatedData.ph_preference = items.some(v => String(v).toLowerCase() === val.toLowerCase())
                                ? items.filter(i => String(i).toLowerCase() !== val.toLowerCase())
                                : [...items, val];
                        } else {
                            const newVal = !checked ? '☑' : 'n';
                            updatedData[key] = newVal;
                        }
                        
                        setFormData(updatedData);
                    }}
                >
                    {checked && <div className="text-plantlix-700 font-bold leading-none select-none" style={{ fontSize: `${coord.h * scale * 0.9}px` }}>✓</div>}
                </div>
            );
        } else {
            const isTextArea = coord.type === 'textarea';
            const Comp = isTextArea ? 'textarea' : 'input';
            const displayValue = formData[key] || (
                key === 'signature_name' ? (user?.full_name || 'Auszubildende(r)') : 
                key === 'signature_instructor' ? 'Ausbilder / Instructor' :
                key === 'signature_date' ? '12.04.2026' : ''
            );
            content = (
                <Comp
                    type={isTextArea ? undefined : 'text'}
                    name={key}
                    value={displayValue}
                    readOnly={debugMode}
                    onChange={handleChange}
                    className={`w-full h-full bg-transparent border-none outline-none hover:bg-plantlix-100/40 focus:bg-white/80 transition-all ${isTextArea ? 'py-1 px-1.5' : 'px-1'} resize-none text-black selection:bg-plantlix-200`}
                    style={{ 
                        pointerEvents: debugMode ? 'none' : 'auto',
                        color: '#000000',
                        lineHeight: isTextArea ? '1.2' : 'normal',
                        display: 'flex',
                        alignItems: isTextArea ? 'flex-start' : 'center',
                        paddingTop: isTextArea ? '2px' : '0'
                    }}
                />
            );
        }

        return (
            <div key={key} style={style} onMouseDown={(e) => handleFieldMouseDown(e, key)}>
                {debugLabel}
                {content}
                {debugMode && isSelected && (
                    <div 
                        className="absolute bottom-0 right-0 w-3 h-3 bg-red-600 cursor-nwse-resize z-[110] flex items-center justify-center rounded-tl shadow-lg"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            setIsResizing(true);
                            setDragStartPos({ x: mousePos.x, y: mousePos.y });
                            setDragSnapshot({ [key]: { w: coord.w, h: coord.h } });
                        }}
                    >
                        <Maximize2 size={8} className="text-white" />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div 
            ref={containerRef} 
            className={`relative w-full shadow-2xl rounded-xl overflow-hidden bg-white min-h-[400px] select-none ${debugMode ? 'ring-4 ring-red-500/20' : ''}`}
            onMouseDown={(e) => {
                // Check if user clicked background area (not a field or calibrator)
                const isOverlayClick = e.target.classList.contains('pdf-content-overlay') || e.target === containerRef.current;
                
                if (canCalibrate && isOverlayClick) {
                    const rect = containerRef.current.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / scale;
                    const y = (e.clientY - rect.top) / scale;
                    setSelectionFrame({ active: true, x1: x, y1: y, x2: x, y2: y });
                    if (!e.shiftKey) setSelectedFields(new Set());
                }
            }}
            onMouseMove={(e) => {
                handleContainerMouseMove(e);
                if (isDraggingCalibrator) {
                    setCalibratorPos({
                        x: e.clientX - calibratorDragStart.x,
                        y: e.clientY - calibratorDragStart.y
                    });
                }
            }}
            onMouseUp={handleGlobalMouseUp}
        >
            {/* Hidden admin trigger for calibration mode */}
            {/* Calibration Controls (Visible if Admin or Dev) */}
            {canCalibrate && (
                <>
                    {/* Secret corner trigger (keep for backward compatibility) */}
                    <div 
                        className={`absolute top-0 left-0 w-16 h-16 z-[200] cursor-crosshair transition-all duration-300 rounded-br-3xl flex items-center justify-center ${
                            debugMode 
                                ? 'bg-red-500/30 shadow-[0_0_20px_rgba(239,68,68,0.4)]' 
                                : 'bg-transparent hover:bg-red-500/20 hover:shadow-[0_0_15px_rgba(239,68,68,0.2)]'
                        }`}
                        onClick={() => setDebugMode(!debugMode)}
                        title="PDF Kalibrierung (Admin/Dev)"
                    >
                        {!debugMode && (
                            <div className="w-2 h-2 rounded-full bg-red-500/10 group-hover:bg-red-500/40 transition-colors animate-pulse" />
                        )}
                    </div>

                    {/* Visible Settings Toggle (Top Right) */}
                    <button 
                        onClick={() => setDebugMode(!debugMode)}
                        className={`absolute top-4 right-4 z-[210] p-2.5 rounded-full shadow-lg transition-all transform hover:scale-110 active:scale-95 ${
                            debugMode 
                                ? 'bg-red-600 text-white rotate-90' 
                                : 'bg-white/90 text-gray-700 hover:text-red-600 border border-gray-200'
                        }`}
                        title="PDF Kalibrierung öffnen"
                    >
                        <Settings className={`w-5 h-5 ${debugMode ? 'animate-spin-slow' : ''}`} />
                    </button>
                </>
            )}

            {/* Selection Frame Visual */}
            {selectionFrame.active && (
                <div 
                    style={{
                        position: 'absolute',
                        left: `${Math.min(selectionFrame.x1, selectionFrame.x2) * scale}px`,
                        top: `${Math.min(selectionFrame.y1, selectionFrame.y2) * scale}px`,
                        width: `${Math.abs(selectionFrame.x1 - selectionFrame.x2) * scale}px`,
                        height: `${Math.abs(selectionFrame.y1 - selectionFrame.y2) * scale}px`,
                        border: '1.5px dashed rgba(239, 68, 68, 0.8)',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        pointerEvents: 'none',
                        zIndex: 1000,
                        borderRadius: '2px'
                    }}
                />
            )}

            {!imageLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 text-gray-400 z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-plantlix-600 mb-4" />
                    <p className="font-semibold text-gray-600">PDF-Vorschau wird vorbereitet...</p>
                </div>
            )}

             <img 
                src={`/static/images/pflanze-der-woche-preview.png?v=${Date.now()}`}
                alt="PDF BG" 
                 className={`w-full h-auto pointer-events-none transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                style={{ 
                    transform: `translate(${bgOffsetH * scale}px, ${bgOffsetV * scale}px) scale(${bgScaleH}, ${bgScaleV})`,
                    transformOrigin: 'top left'
                }}
                onLoad={() => setImageLoaded(true)}
             />
            
            <div className={`absolute inset-0 pdf-content-overlay transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}>
                {/* 50pt DEBUG DOT GRID */}
                {debugMode && showGridInEditor && (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none z-[5]" style={{ overflow: 'visible' }}>
                        {Array.from({ length: Math.ceil(PDF_WIDTH / 50) + 1 }).map((_, i) => (
                            Array.from({ length: Math.ceil(PDF_HEIGHT / 50) + 1 }).map((_, j) => (
                                <circle 
                                    key={`dot-${i}-${j}`} 
                                    // Grid dots must also reflect the global offsets to stay in sync with boxes
                                    cx={(i * 50 * exportScaleX + exportNudgeX) * scale} 
                                    cy={(j * 50 * exportScaleY + exportNudgeY) * scale} 
                                    r={1.5} 
                                    fill="rgba(255, 0, 0, 0.5)" 
                                />
                            ))
                        ))}
                    </svg>
                )}
                {Object.entries(localCoords).map(([key, coord]) => renderField(key, coord))}
            </div>

            {/* ADVANCED ADMIN CALIBRATION OVERLAY */}
            {debugMode && canCalibrate && (
                <div 
                    style={{ left: `${calibratorPos.x}px`, top: `${calibratorPos.y}px` }}
                    className="fixed bg-gray-900/95 text-white p-4 rounded-xl z-[1000] w-72 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-md border border-white/10 select-none animate-in fade-in slide-in-from-top-2"
                >
                    <div 
                        onMouseDown={handleCalibratorMouseDown}
                        className="flex items-center justify-between mb-4 border-b border-white/10 pb-2 cursor-move group"
                    >
                        <div className="flex items-center gap-2">
                            <Move className="w-4 h-4 text-red-500 group-hover:scale-110 transition-transform" />
                            <span className="font-bold text-xs tracking-wider uppercase opacity-80">PDF CALIBRATOR</span>
                        </div>
                        <div className="flex gap-1">
                            <button onClick={() => setIsMinimized(!isMinimized)} className="hover:bg-white/10 p-1 rounded transition-colors text-gray-400">
                                {isMinimized ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                            </button>
                            <button onClick={() => setDebugMode(false)} className="hover:bg-red-500/20 p-1 rounded transition-colors text-red-400">
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>

                    {!isMinimized && (
                        <div className="space-y-4 animate-in slide-in-from-top-1 duration-200">
                            <div className="flex justify-between items-center text-[11px] bg-black/30 px-2 py-1.5 rounded font-mono">
                                <span className="text-gray-500">CANVAS 🎯</span>
                                <span className="text-plantlix-500">X:{Math.round(mousePos.x)} Y:{Math.round(mousePos.y)}</span>
                            </div>

                            {/* ADD ELEMENTS & UTILS */}
                            <div className="space-y-3">
                                <button 
                                    onClick={() => toggleSection('add')}
                                    className="w-full flex items-center justify-between text-[9px] text-gray-400 uppercase font-black tracking-widest px-1 hover:text-white transition-colors"
                                >
                                    <span>Elemente & Funktionen</span>
                                    {collapsedSections.add ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
                                </button>
                                
                                {!collapsedSections.add && (
                                    <div className="space-y-2">
                                        <div className="grid grid-cols-2 gap-2">
                                            <button 
                                                onClick={() => handleAddElement('text')}
                                                className="flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 p-2 rounded text-[10px] font-bold transition-all border border-white/5"
                                            >
                                                <Type size={12} className="text-gray-400" /> Text
                                            </button>
                                            <button 
                                                onClick={() => handleAddElement('checkbox')}
                                                className="flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 p-2 rounded text-[10px] font-bold transition-all border border-white/5"
                                            >
                                                <CheckSquare size={12} className="text-gray-400" /> Check
                                            </button>
                                        </div>
                                        <button 
                                            onClick={handleSelectAll}
                                            className="w-full flex items-center justify-center gap-1.5 bg-plantlix-500/10 hover:bg-plantlix-500/20 text-plantlix-400 p-2 rounded text-[10px] font-bold transition-all border border-plantlix-500/20"
                                        >
                                            <MousePointer2 size={12} /> Alle Markieren ({Object.keys(localCoords).filter(k => !k.startsWith('_')).length})
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* RASTER SETTINGS */}
                            <div className="space-y-3">
                                <button 
                                    onClick={() => toggleSection('raster')}
                                    className="w-full flex items-center justify-between text-[9px] text-gray-400 uppercase font-black tracking-widest px-1 hover:text-white transition-colors border-t border-white/5 pt-3"
                                >
                                    <span>Raster Einstellungen</span>
                                    {collapsedSections.raster ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
                                </button>

                                {!collapsedSections.raster && (
                                    <div className="space-y-2 px-1">
                                        <label className="flex items-center gap-2 group cursor-pointer">
                                            <input type="checkbox" checked={showGridInEditor} onChange={(e) => setShowGridInEditor(e.target.checked)} className="rounded border-gray-600 bg-gray-800 text-red-500 focus:ring-0" />
                                            <span className="text-[10px] text-gray-300 group-hover:text-white transition-colors">Raster im Editor zeigen</span>
                                        </label>
                                        <label className="flex items-center gap-2 group cursor-pointer">
                                            <input type="checkbox" checked={showGridInExport} onChange={(e) => setShowGridInExport(e.target.checked)} className="rounded border-gray-600 bg-gray-800 text-red-500 focus:ring-0" />
                                            <span className="text-[10px] text-gray-300 group-hover:text-white transition-colors">Raster im Export (PDF) zeigen</span>
                                        </label>
                                    </div>
                                )}
                            </div>

                            {/* BACKGROUND CALIBRATION */}
                            <div className="space-y-3">
                                <button 
                                    onClick={() => toggleSection('bg')}
                                    className="w-full flex items-center justify-between text-[9px] text-gray-400 uppercase font-black tracking-widest px-1 hover:text-white transition-colors border-t border-white/5 pt-3"
                                >
                                    <span>Bild Kalibrierung (Editor)</span>
                                    {collapsedSections.bg ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
                                </button>

                                {!collapsedSections.bg && (
                                    <div className="space-y-3 px-1">
                                        <div className="space-y-1">
                                            <label className="text-[9px] text-gray-400 flex justify-between">
                                                <span>Vertikal (Y)</span>
                                                <span className={bgOffsetV !== 0 ? 'text-red-400' : 'text-gray-600'}>{bgOffsetV}pt</span>
                                            </label>
                                            <input 
                                                type="range" min="-100" max="100" step="0.5" 
                                                value={bgOffsetV} 
                                                onChange={(e) => setBgOffsetV(parseFloat(e.target.value))} 
                                                className="w-full accent-red-500" 
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] text-gray-400 flex justify-between">
                                                <span>Skalierung Vertikal</span>
                                                <span className={bgScaleV !== 1 ? 'text-red-400' : 'text-gray-600'}>{bgScaleV.toFixed(3)}</span>
                                            </label>
                                            <input 
                                                type="range" min="0.8" max="1.2" step="0.001" 
                                                value={bgScaleV} 
                                                onChange={(e) => setBgScaleV(parseFloat(e.target.value))} 
                                                className="w-full accent-red-500" 
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] text-gray-400 flex justify-between">
                                                <span>Skalierung Horizontal</span>
                                                <span className={bgScaleH !== 1 ? 'text-red-400' : 'text-gray-600'}>{bgScaleH.toFixed(3)}</span>
                                            </label>
                                            <input 
                                                type="range" min="0.8" max="1.2" step="0.001" 
                                                value={bgScaleH} 
                                                onChange={(e) => setBgScaleH(parseFloat(e.target.value))} 
                                                className="w-full accent-red-500" 
                                            />
                                        </div>
                                        <button 
                                            onClick={() => { setBgOffsetV(0); setBgOffsetH(0); setBgScaleV(1); setBgScaleH(1); }}
                                            className="w-full py-1 text-[8px] uppercase font-bold text-gray-500 hover:text-white border border-white/5 rounded transition-colors"
                                        >
                                            Reset Bild-Position
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* EXPORT NUDGING */}
                            <div className="space-y-3">
                                <button 
                                    onClick={() => toggleSection('export')}
                                    className="w-full flex items-center justify-between text-[9px] text-gray-400 uppercase font-black tracking-widest px-1 hover:text-white transition-colors border-t border-white/5 pt-3"
                                >
                                    <span className="text-plantlix-400">PDF Export Korrektur</span>
                                    {collapsedSections.export ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
                                </button>

                                {!collapsedSections.export && (
                                    <div className="space-y-3 px-1">
                                        <div className="space-y-1">
                                            <label className="text-[9px] text-gray-400 flex justify-between">
                                                <span>Export Offset Y (Vertikal)</span>
                                                <span className={exportNudgeY !== 0 ? 'text-plantlix-400' : 'text-gray-600'}>{exportNudgeY}pt</span>
                                            </label>
                                            <input 
                                                type="range" min="-100" max="100" step="1" 
                                                value={exportNudgeY} 
                                                onChange={(e) => setExportNudgeY(parseInt(e.target.value))} 
                                                className="w-full accent-plantlix-500" 
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] text-gray-400 flex justify-between">
                                                <span>Export Offset X (Horizontal)</span>
                                                <span className={exportNudgeX !== 0 ? 'text-plantlix-400' : 'text-gray-600'}>{exportNudgeX}pt</span>
                                            </label>
                                            <input 
                                                type="range" min="-100" max="100" step="1" 
                                                value={exportNudgeX} 
                                                onChange={(e) => setExportNudgeX(parseInt(e.target.value))} 
                                                className="w-full accent-plantlix-500" 
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] text-gray-400 flex justify-between">
                                                <span>Export Skalierung Y</span>
                                                <span className={exportScaleY !== 1 ? 'text-plantlix-400' : 'text-gray-600'}>{exportScaleY.toFixed(3)}</span>
                                            </label>
                                            <input 
                                                type="range" min="0.8" max="1.2" step="0.001" 
                                                value={exportScaleY} 
                                                onChange={(e) => setExportScaleY(parseFloat(e.target.value))} 
                                                className="w-full accent-plantlix-500" 
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[9px] text-gray-400 flex justify-between">
                                                <span>Export Skalierung X</span>
                                                <span className={exportScaleX !== 1 ? 'text-plantlix-400' : 'text-gray-600'}>{exportScaleX.toFixed(3)}</span>
                                            </label>
                                            <input 
                                                type="range" min="0.8" max="1.2" step="0.001" 
                                                value={exportScaleX} 
                                                onChange={(e) => setExportScaleX(parseFloat(e.target.value))} 
                                                className="w-full accent-plantlix-500" 
                                            />
                                        </div>
                                        <button 
                                            onClick={() => { setExportNudgeX(0); setExportNudgeY(0); setExportScaleX(1); setExportScaleY(1); }}
                                            className="w-full py-1 mt-2 text-[8px] uppercase font-bold text-gray-500 hover:text-red-400 border border-white/5 hover:border-red-500/30 rounded transition-colors"
                                        >
                                            Reset Export-Offset
                                        </button>
                                    </div>
                                )}
                            </div>

                            <button 
                                onClick={handleSaveGlobal}
                                disabled={isSaving}
                                className="w-full py-2 bg-red-600 hover:bg-red-500 text-white text-[10px] font-black uppercase tracking-widest rounded shadow-xl transition-all flex items-center justify-center gap-2 mt-4"
                            >
                                <Save className="w-3 h-3" />
                                {saveStatus || "Global Persistieren"}
                            </button>

                            {selectedFields.size > 0 && (
                                <div className="space-y-3 bg-white/5 p-3 rounded-lg border border-white/5 animate-in zoom-in-95 duration-150 mt-4">
                                    <button 
                                        onClick={() => toggleSection('field')}
                                        className="w-full flex items-center justify-between text-[9px] text-white uppercase font-black tracking-widest group"
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-plantlix-500" />
                                            <span>Feld Einstellungen</span>
                                            <span className="text-[8px] text-gray-500 lowercase font-normal italic">({selectedFields.size} ausgewählt)</span>
                                        </div>
                                        {collapsedSections.field ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                                    </button>

                                    {!collapsedSections.field && (
                                        <>
                                            {(() => {
                                                const firstKey = Array.from(selectedFields)[0];
                                                const coord = localCoords[firstKey];
                                                const isSingle = selectedFields.size === 1;

                                                return (
                                                    <div className="space-y-3 pt-1">
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className="space-y-1">
                                                                <label className="text-[8px] text-gray-500 uppercase tracking-tighter">Breite (W)</label>
                                                                <input type="number" value={Math.round(coord.w)} onChange={(e) => updateManualCoord(firstKey, 'w', e.target.value)} className="w-full bg-black/40 border border-white/10 rounded px-1.5 py-1 text-[10px] outline-none focus:border-plantlix-500/50" />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <label className="text-[8px] text-gray-500 uppercase tracking-tighter">Höhe (H)</label>
                                                                <input type="number" value={Math.round(coord.h)} onChange={(e) => updateManualCoord(firstKey, 'h', e.target.value)} className="w-full bg-black/40 border border-white/10 rounded px-1.5 py-1 text-[10px] outline-none focus:border-plantlix-500/50" />
                                                            </div>
                                                        </div>

                                                        <div className="space-y-1">
                                                            <label className="text-[8px] text-gray-500 uppercase tracking-tighter flex items-center justify-between">
                                                                <span>Schriftgröße</span>
                                                                <span className="text-plantlix-400">{coord.fs || 9}pt</span>
                                                            </label>
                                                            <input 
                                                                type="range" min="5" max="36" step="0.5" 
                                                                value={coord.fs || 9} 
                                                                onChange={(e) => updateManualCoord(firstKey, 'fs', e.target.value)} 
                                                                className="w-full accent-plantlix-500" 
                                                            />
                                                        </div>

                                                        <div className="space-y-1">
                                                            <label className="text-[8px] text-gray-500 uppercase tracking-tighter flex items-center justify-between">
                                                                <span>Feld Nudge Y (Vertikal)</span>
                                                                <span className="text-plantlix-400">{coord.ny || 0}pt</span>
                                                            </label>
                                                            <input 
                                                                type="range" min="-30" max="30" step="0.5" 
                                                                value={coord.ny || 0} 
                                                                onChange={(e) => updateManualCoord(firstKey, 'ny', e.target.value)} 
                                                                className="w-full accent-plantlix-500" 
                                                            />
                                                        </div>

                                                        <div className="space-y-1">
                                                            <label className="text-[8px] text-gray-500 uppercase tracking-tighter flex items-center justify-between">
                                                                <span>Feld Nudge X (Horizontal)</span>
                                                                <span className="text-plantlix-400">{coord.nx || 0}pt</span>
                                                            </label>
                                                            <input 
                                                                type="range" min="-30" max="30" step="0.5" 
                                                                value={coord.nx || 0} 
                                                                onChange={(e) => updateManualCoord(firstKey, 'nx', e.target.value)} 
                                                                className="w-full accent-plantlix-500" 
                                                            />
                                                        </div>

                                                        {isSingle && (
                                                            <div className="space-y-1">
                                                                <label className="text-[8px] text-gray-500 uppercase tracking-tighter flex items-center gap-1"><ArrowRightLeft size={8} /> Mapping</label>
                                                                <select 
                                                                    className="w-full bg-black/40 border border-white/10 rounded px-1.5 py-1 text-[10px] outline-none focus:border-plantlix-500/50"
                                                                    value={AVAILABLE_FIELDS.find(f => f.key === firstKey) ? firstKey : ""}
                                                                    onChange={(e) => handleRemap(firstKey, e.target.value)}
                                                                >
                                                                    <option value="">-- Manuell ({firstKey}) --</option>
                                                                    {AVAILABLE_FIELDS.map(f => (
                                                                        <option key={f.key} value={f.key}>{f.label}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            <div className="grid grid-cols-2 gap-2 mt-2">
                                                <button 
                                                    onClick={() => handleGroupAction('group')}
                                                    disabled={selectedFields.size < 2}
                                                    className="flex items-center justify-center gap-1.5 bg-plantlix-500 hover:bg-plantlix-500 disabled:opacity-20 p-2 rounded text-[10px] font-bold transition-all shadow-lg"
                                                >
                                                    <Link className="w-3 h-3" /> Gruppe
                                                </button>
                                                <button 
                                                    onClick={() => handleGroupAction('ungroup')}
                                                    className="flex items-center justify-center gap-1.5 bg-gray-700 hover:bg-gray-600 p-2 rounded text-[10px] font-bold transition-all"
                                                >
                                                    <Unlink className="w-3 h-3" /> Lösen
                                                </button>
                                            </div>
                                            {selectedFields.size === 1 && (
                                                <button 
                                                    onClick={() => handleDeleteElement(Array.from(selectedFields)[0])}
                                                    className="w-full flex items-center justify-center gap-1.5 bg-red-900/30 hover:bg-red-900/50 border border-red-500/30 p-1.5 rounded text-[9px] font-bold transition-all mt-1"
                                                >
                                                    <Trash2 className="w-3 h-3" /> Element löschen
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            <div className="pt-2">
                                <button 
                                    onClick={handleSaveGlobal}
                                    disabled={isSaving}
                                    className={`w-full flex items-center justify-center gap-2 p-3 rounded-lg font-black text-xs tracking-widest transition-all shadow-2xl ${
                                        saveStatus?.includes('successfully') 
                                            ? 'bg-plantlix-600 ring-2 ring-plantlix-400 scale-[1.02]' 
                                            : saveStatus?.includes('Error') || saveStatus?.includes('Connection')
                                                ? 'bg-rose-600 animate-shake' 
                                                : 'bg-red-600 hover:bg-red-500 active:scale-95'
                                    }`}
                                >
                                    <Save className={`w-4 h-4 ${isSaving ? 'animate-spin' : ''}`} /> 
                                    {isSaving ? 'UPLOADING...' : 'GLOBAL PERSISTIEREN'}
                                </button>
                                {saveStatus && (
                                    <div className={`flex items-center justify-center gap-1.5 mt-3 py-1 px-2 rounded-full text-[9px] font-black uppercase tracking-tighter ${
                                        saveStatus.includes('Error') || saveStatus.includes('Connection') ? 'bg-rose-500/20 text-rose-400' : 'bg-plantlix-500/20 text-plantlix-400'
                                    }`}>
                                        <AlertTriangle className="w-3 h-3 shrink-0" />
                                        {saveStatus}
                                    </div>
                                )}
                            </div>

                            <div className="bg-yellow-500/5 p-2.5 rounded-lg border border-yellow-500/20 space-y-1.5">
                                <div className="flex items-center gap-1.5 text-[9px] text-yellow-500 font-black uppercase tracking-widest">
                                    <AlertTriangle className="w-3 h-3" /> Calibration Tip
                                </div>
                                <p className="text-[9px] text-yellow-500/60 leading-tight">
                                    Dragging auf dem Header verschiebt dieses Fenster. <br/>
                                    Nutze den + Button für neue PDF-Elemente.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* Image Editor Modal */}
            {editingImageKey && (
                <ImageEditorModal
                    image={editingImageKey === 'image_1' ? image1Preview : image2Preview}
                    initialTransform={formData?.[`${editingImageKey}_transform`] || {}}
                    aspect={localCoords[editingImageKey]?.w / localCoords[editingImageKey]?.h}
                    onCancel={() => setEditingImageKey(null)}
                    onSave={(transform) => {
                        setFormData(prev => ({
                            ...prev,
                            [`${editingImageKey}_transform`]: transform
                        }));
                        setEditingImageKey(null);
                    }}
                    onDelete={() => {
                        setFormData(prev => ({
                            ...prev,
                            [`${editingImageKey}_transform`]: { 
                                ...(prev?.[`${editingImageKey}_transform`] || {}),
                                is_deleted: true 
                            }
                        }));
                        setEditingImageKey(null);
                    }}
                    onChangeImage={() => {
                        const num = editingImageKey === 'image_1' ? 1 : 2;
                        document.getElementById(`pdf-file-input-${num}`).click();
                        setEditingImageKey(null);
                    }}
                />
            )}
        </div>
    );
}
